import {useMutation, useQueryClient} from "@tanstack/react-query";
import {useClientV2} from "../../../../utils/hooks";
import {message} from "antd";
import {GET_STORE_SUBSCRIPTION_RQ_KEY} from "../constants";
import {invalidateSubscriptionListQueries} from "../invalidate-subscription-lists";
import type {UpdateSubscriptionTierItemPayload} from "../tiers/types";

export type UpdateSubscriptionApiPayload = {
  name?: string;
  description?: string;
  hideFromProfile?: boolean;
  requestPhoneNumber?: boolean;
  redirectUrl?: string;
  bookingQuestions?: string[];
  enableCurrencyConversion?: boolean;
  selectedCurrencies?: string[];
  /** Existing tiers carry `id`; new tiers omit `id`. Backend upserts on PATCH. */
  tiers?: UpdateSubscriptionTierItemPayload[];
};

export type UpdateSubscriptionApiRawResponse = {
  status: boolean;
  message: string;
  data: {
    id: number;
    name: string;
    description: string;
    hideFromProfile: boolean;
    redirectUrl: string;
    bookingQuestions: string[];
    updatedAt: string;
  };
};

/** PATCH body: omit undefined keys so partial updates match API expectations. */
function compactUpdatePayload(
  payload: UpdateSubscriptionApiPayload
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(payload).filter(([, value]) => value !== undefined)
  );
}

export function formatUpdateSubscriptionError(error: unknown) {
  if (error && typeof error === "object" && "message" in error) {
    return String((error as {message: unknown}).message);
  }
  return "Could not update subscription.";
}

export const useUpdateSubscriptionApi = () => {
  const queryClient = useQueryClient();
  const client = useClientV2();

  return useMutation({
    mutationFn: async ({
      subscriptionId,
      payload,
    }: {
      subscriptionId: number;
      payload: UpdateSubscriptionApiPayload;
    }) => {
      // Same path as GET (useGetSubscriptionApi); updates must use PATCH, not GET.
      const raw = await client(`subscriptions/${subscriptionId}`, {
        method: "PATCH",
        data: compactUpdatePayload(payload),
      });
      return raw as UpdateSubscriptionApiRawResponse;
    },
    onError: (error: unknown) => {
      message.error(formatUpdateSubscriptionError(error));
    },
    onSettled: (_, __, {subscriptionId}) => {
      queryClient.invalidateQueries({
        queryKey: [GET_STORE_SUBSCRIPTION_RQ_KEY, String(subscriptionId)],
      });
      invalidateSubscriptionListQueries(queryClient);
    },
  });
};
